import type {
	GroupedParametrics,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";

/**
 * Normalize string for URL: lowercase, spaces to hyphen, alphanumeric and hyphens only.
 */
export function slugify(value: string): string {
	if (!value) return "";
	return value
		.toLowerCase()
		.trim()
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.replace(/\s+/g, "-")
		.replace(/[^a-z0-9-]/g, "")
		.replace(/-+/g, "-")
		.replace(/^-|-$/g, "");
}

/**
 * Convert ParametricListSeed (e.g. "10:19,1:1,2:11") to query object (e.g. { color: "white", size: "xs" }).
 * Uses allParametrics to resolve parametricId:valueId to group slug and value slug.
 */
export function parametricsSeedToQuery(
	seed: string | undefined,
	allParametrics: ParametricListValue[]
): Record<string, string> {
	const result: Record<string, string> = {};
	if (!seed || !allParametrics?.length) return result;

	const pairs = seed.split(",");
	for (const pair of pairs) {
		const [pidStr, vidStr] = pair.split(":");
		const pid = parseInt(pidStr ?? "", 10);
		const vid = parseInt(vidStr ?? "", 10);
		if (Number.isNaN(pid) || Number.isNaN(vid)) continue;

		const parametric = allParametrics.find(
			(p) => p.ParametricId === pid && p.Id === vid
		);
		if (!parametric) continue;

		const groupSlug = parametric.ParametricCode.split("-")[0]?.toLowerCase();
		const valueSlug = slugify(parametric.Name || parametric.Code);
		if (groupSlug && valueSlug) {
			result[groupSlug] = valueSlug;
		}
	}
	return result;
}

/**
 * Resolve route query (e.g. { color: "white", size: "xs" }) to selected color and second parametric
 * using product's GroupedParametrics. Matches by slugified Code or Name.
 */
export function queryToSelectedParametrics(
	query: Record<string, unknown>,
	groupParametrics: GroupedParametrics
): {
	selectedColor?: ParametricListValue;
	selectedSecondParametric?: ParametricListValue;
} {
	const result: {
		selectedColor?: ParametricListValue;
		selectedSecondParametric?: ParametricListValue;
	} = {};
	const keys = Object.keys(groupParametrics);
	if (!keys.length) return result;

	const normalizedQuery: Record<string, string> = {};
	for (const key of Object.keys(query)) {
		const v = query[key];
		const str = Array.isArray(v) ? (v[0] as string) : (v as string);
		if (str) normalizedQuery[key.toLowerCase()] = str;
	}

	const colorKey = keys.find((k) => k === "color");
	const secondKey = keys.filter((k) => k !== "color")[0];

	const findInGroup = (groupKey: string): ParametricListValue | undefined => {
		const slug = normalizedQuery[groupKey];
		if (!slug) return undefined;
		const list = groupParametrics[groupKey];
		if (!Array.isArray(list)) return undefined;
		return list.find(
			(item) =>
				slugify(
					(item as ParametricListValue).Name ||
						(item as ParametricListValue).Code
				) === slug
		) as ParametricListValue | undefined;
	};

	if (colorKey) {
		result.selectedColor = findInGroup(colorKey);
	}
	if (secondKey) {
		result.selectedSecondParametric = findInGroup(secondKey);
	}
	return result;
}
